// frontend/src/components/cages/CageBreedingModals.js
// The two "start a breeding record on this cage" overlays: add a litter, and
// record a pregnancy.
//
// They are co-located because they were near-duplicates. Both asked for a
// mother and a father from the same cage, and both built the father <select>
// with the same In cage / Previously in cage optgroup logic — that is now one
// ParentSelect, used four times.
//
// Presentation only: all state and handlers live in CageManagement.

import React from 'react';

// Shared centered-overlay chrome.
function ModalShell({ children }) {
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', maxWidth: '500px', width: '90%', maxHeight: '90%', overflowY: 'auto' }}>
        {children}
      </div>
    </div>
  );
}

const selectStyle = { width: '100%', padding: '8px', boxSizing: 'border-box' };

// A parent picker for one cage. When `previousParents` is given, mice that
// have since left the cage are offered in a second optgroup — a litter can be
// attributed to a parent that was moved out or archived after the mating.
function ParentSelect({
  label,
  value,
  onChange,
  mice,
  cageId,
  sex,
  formatMouse,
  previousParents,
  hint,
}) {
  const inCage = mice.filter(
    (m) => String(m.cage_id) === String(cageId) && m.sex === sex && m.status === 'Active'
  );

  let previous = [];
  if (previousParents) {
    const inCageIds = new Set(inCage.map((m) => m.id));
    previous = previousParents.filter((m) => !inCageIds.has(m.id));
  }

  return (
    <div className="form-group">
      <label>{label} *</label>
      <select value={value} onChange={onChange} required style={selectStyle}>
        <option value="">Select {label}...</option>
        {previousParents ? (
          <>
            {inCage.length > 0 && (
              <optgroup label="In cage">
                {inCage.map((mouse) => (
                  <option key={mouse.id} value={mouse.id}>
                    {formatMouse(mouse)}
                  </option>
                ))}
              </optgroup>
            )}
            {previous.length > 0 && (
              <optgroup label="Previously in cage">
                {previous.map((mouse) => (
                  <option key={mouse.id} value={mouse.id} disabled={mouse.status !== 'Active'}>
                    {mouse.display_cage_number ? `${mouse.display_cage_number}; ` : ''}
                    {mouse.physical_tag || 'No Tag'}
                    {mouse.status !== 'Active' ? ' (archived)' : ''}
                  </option>
                ))}
              </optgroup>
            )}
          </>
        ) : (
          inCage.map((mouse) => (
            <option key={mouse.id} value={mouse.id}>
              {formatMouse(mouse)}
            </option>
          ))
        )}
      </select>
      {hint && <small>{hint}</small>}
    </div>
  );
}

const PAIR_HINT = 'If the cage contains a pair, they will be pre-selected.';

export function AddLitterModal({
  litterModalData,
  setLitterModalData,
  previousMales,
  mice,
  formatMouse,
  onSubmit,
  isSubmitting,
  onCancel,
  error,
}) {
  return (
    <ModalShell>
      <h3>Add Litter to Cage: {litterModalData.cageDisplayNumber}</h3>

      {error && <div className="error">{error}</div>}

      <form onSubmit={onSubmit}>
        <div className="form-group">
          <label>Date of Birth *</label>
          <input
            type="date"
            value={litterModalData.date_of_birth}
            onChange={(e) => setLitterModalData({ ...litterModalData, date_of_birth: e.target.value })}
            required
            style={selectStyle}
          />
        </div>
        <div className="form-group">
          <label>Estimated Initial Pup Count *</label>
          <input
            type="number"
            value={litterModalData.initial_pup_count}
            placeholder="1"
            onChange={(e) => {
              const value = e.target.value;
              setLitterModalData({
                ...litterModalData,
                initial_pup_count: value === '' ? '' : parseInt(value, 10),
              });
            }}
            min="1"
            required
            style={selectStyle}
          />
        </div>

        <ParentSelect
          label="Mother"
          value={litterModalData.mother_id}
          onChange={(e) => setLitterModalData({ ...litterModalData, mother_id: e.target.value })}
          mice={mice}
          cageId={litterModalData.cageId}
          sex="Female"
          formatMouse={formatMouse}
        />
        <ParentSelect
          label="Father"
          value={litterModalData.father_id}
          onChange={(e) => setLitterModalData({ ...litterModalData, father_id: e.target.value })}
          mice={mice}
          cageId={litterModalData.cageId}
          sex="Male"
          formatMouse={formatMouse}
          previousParents={previousMales}
          hint={PAIR_HINT}
        />

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button type="submit" style={{ flex: 1 }} disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Litter'}
          </button>
          <button type="button" onClick={onCancel} className="secondary-button" style={{ flex: 1 }}>
            Cancel
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

export function PregnancyModal({
  pregnancyModalData,
  setPregnancyModalData,
  previousMales,
  mice,
  formatMouse,
  onSubmit,
  isSubmitting,
  onCancel,
  error,
}) {
  return (
    <ModalShell>
      <h3>Record Pregnancy — Cage: {pregnancyModalData.cageDisplayNumber}</h3>

      {error && <div className="error">{error}</div>}

      <form onSubmit={onSubmit}>
        <ParentSelect
          label="Mother"
          value={pregnancyModalData.mother_id}
          onChange={(e) => setPregnancyModalData({ ...pregnancyModalData, mother_id: e.target.value })}
          mice={mice}
          cageId={pregnancyModalData.cageId}
          sex="Female"
          formatMouse={formatMouse}
        />
        <ParentSelect
          label="Father"
          value={pregnancyModalData.father_id}
          onChange={(e) => setPregnancyModalData({ ...pregnancyModalData, father_id: e.target.value })}
          mice={mice}
          cageId={pregnancyModalData.cageId}
          sex="Male"
          formatMouse={formatMouse}
          previousParents={previousMales}
          hint={PAIR_HINT}
        />

        <div className="form-group">
          <label>Breeding Start Date *</label>
          <input
            type="date"
            value={pregnancyModalData.breeding_start_date}
            onChange={(e) =>
              setPregnancyModalData({ ...pregnancyModalData, breeding_start_date: e.target.value })
            }
            required
            style={selectStyle}
          />
          <small>Expected due date: ~20 days from start</small>
        </div>
        <div className="form-group">
          <label>Notes</label>
          <textarea
            value={pregnancyModalData.notes}
            onChange={(e) => setPregnancyModalData({ ...pregnancyModalData, notes: e.target.value })}
            placeholder="Optional notes..."
            style={{ ...selectStyle, minHeight: '60px' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button type="button" onClick={onCancel} className="secondary-button" style={{ flex: 1 }}>
            Cancel
          </button>
          <button type="submit" style={{ flex: 1 }} disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Record Pregnancy'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
